import React, { useContext, useEffect, useState }  from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { API } from 'aws-amplify';
import { v1 as uuidv1 } from 'uuid';
import { ColorSwatches } from '../cardColors';
import BoardLookup from '../BoardLookup';

import './NewCard.css';
import { useI18n } from '../../../../i18n';

const stopBubble = (event) => {
    if (event && event.stopPropagation) event.stopPropagation();
};

const NewCard = props => {
    const { t } = useI18n();
    const { userFullname, taskList } = useContext(BoardLookup);
    const [tasks, setTasks] = useState([]);
    const [task, setTask] = useState('');
    const [districts, setDistricts] = useState([]);
    const [district, setDistrict] = useState('');
    const [assets, setAssets] = useState([]);
    const [asset, setAsset] = useState('');
    const [assetFiltered, setAssetsFiltered] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [body, setBody] = useState('');
    const [cardColor, setCardColor] = useState('note-white');
    const params = { 
        headers: {
            'x-api-key': process.env.REACT_APP_API_KEY,        
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
    }

    useEffect(()=>{
        let cancelled = false;
        const fetchTasks = async () => {    
            const next = await API.get('task_active','/', params);
            if (!cancelled) setTasks(Array.isArray(next) ? next : []);
        }

        const fetchDistrict = async () => {	
            const dists = await API.get('settings','/DISTRICTS', params);
            const values = (dists && dists.values ? [...dists.values] : []).sort((a, b) => (a > b) ? 1 : -1);
            if (!cancelled) setDistricts(values);
        }
        
        const fetchAssets = async () => {    
            const next = await API.get('assets','/', params);
            const list = Array.isArray(next) ? [...next] : [];
            list.sort((a, b) => (a.site_ref > b.site_ref) ? 1 : -1);
            if (!cancelled) {
                setAssets(list);
                setAssetsFiltered(list);
            }
        }

        setIsLoading(true);  
        Promise.all([fetchTasks(), fetchDistrict(), fetchAssets()])
            .catch(() => {})
            .finally(() => { if (!cancelled) setIsLoading(false); });
        return () => { cancelled = true; };
        }, []);

    const handleFilterDistrict = (selDistrict) => {
        setAssetsFiltered(selDistrict
            ? assets.filter(item => item.district === selDistrict)
            : assets);
    }

    const cancelCard = e => {
        stopBubble(e);
        if (e && e.preventDefault) e.preventDefault();
        props.onCancel(e);
      }

    const handleTaskChange = (taskId) => {
        setTask(taskId);
        const taskSel = tasks.find((item) => item.taskId === taskId);
        if (!taskSel) return;
        if (taskSel.district) {
            setDistrict(taskSel.district);
            handleFilterDistrict(taskSel.district);
        }
        if (taskSel.assetId) setAsset(taskSel.assetId);
        if (!body && taskSel.fault_description) setBody(taskSel.fault_description);
    };

    const addCard = e => {
        stopBubble(e);
        if (e && e.preventDefault) e.preventDefault();
        const taskSel = tasks.find(t => t.taskId === task);
        const siteSel = assets.find(t => t.assetId === asset);
        const note = (body || '').trim() || (taskSel && taskSel.fault_description) || '';
        if (!note) return;

        const card = {
            id: uuidv1(),
            title: note,
            name: userFullname || '',
            dateCreated: new Date().toISOString(),
            body: note,
            cardColor,
        };
        if (task) card.taskId = task;
        if (taskSel && taskSel.task_ref) card.task = taskSel.task_ref;
        const assetId = asset || (taskSel && taskSel.assetId);
        if (assetId) card.assetId = assetId;
        const siteRef = (siteSel && siteSel.site_ref) || (taskSel && taskSel.site_ref);
        if (siteRef) card.asset = siteRef;
        const nextDistrict = district || (taskSel && taskSel.district);
        if (nextDistrict) card.district = nextDistrict;
        if (taskSel && taskSel.category) card.category = taskSel.category;
        if (taskSel && taskSel.type_fault) card.type_fault = taskSel.type_fault;
        if (taskSel && taskSel.urgent_fault) card.urgent_fault = taskSel.urgent_fault;
        if (taskSel && taskSel.sla_attend) card.sla_attend = taskSel.sla_attend;
        const address = (siteSel && siteSel.site_address) || (taskSel && taskSel.site_address);
        if (address) card.site_address = address;

        props.onAdd(card);
      }

    return (
      <div className="NewCard" onMouseDown={stopBubble} onClick={stopBubble}>
        {isLoading ? (
            <p className="NewCard-loading">{t('common.loading')}</p>
        ) : (
        <div className="NewCard-form">
            <Form.Control
                as="textarea"
                rows="3"
                placeholder={t('board.needsAttention')}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="NewCard-textarea"
            />
            <Form.Control
                as="select"
                value={task}
                onChange={e => handleTaskChange(e.target.value)}
                className="NewCard-select"
            >
                <option value="">{t('board.chooseTask')}</option>
                {(tasks.length ? tasks : taskList || []).map(item => (
                    <option key={item.taskId} value={item.taskId}>{item.task_ref}</option>
                ))}
            </Form.Control>
            <Form.Control
                as="select"
                value={district}
                onChange={e => {
                    setDistrict(e.target.value);
                    handleFilterDistrict(e.target.value);
                }}
                className="NewCard-select"
            >
                <option value="">{t('common.district')}</option>
                {districts.map((item) => (
                    <option key={item} value={item}>{item}</option>
                ))}
            </Form.Control>
            <Form.Control
                as="select"
                value={asset}
                onChange={e => setAsset(e.target.value)}
                className="NewCard-select"
            >
                <option value="">{t('nav.assets')}</option>
                {assetFiltered.map(item => (
                    <option key={item.assetId} value={item.assetId}>{item.site_ref}</option>
                ))}
            </Form.Control>
            <ColorSwatches value={cardColor} onChange={setCardColor} />
            <div className="NewCard-actions">
                <Button type="button" variant="primary" size="sm" onClick={addCard}>{t('common.save')}</Button>
                <Button type="button" variant="secondary" size="sm" onClick={cancelCard}>{t('common.cancel')}</Button>
            </div>
        </div>
        )}
      </div>
    )
  }

export default NewCard;
